import {
  PayloadPutMerchantSettings,
  ResponseGetMerchantSettings,
  RouteGetMerchantSettings,
  RoutePutMerchantSettings,
} from "@CommonApi/merchant-settings";
import {
  ResponseGetReportPowerBIWorkspaceReports,
  RouteGetReportPowerBIWorkspaceReports,
} from "@CommonApi/report/powerbi/workspace/reports";
import { RoutePostReportPowerBIWorkspace } from "@CommonApi/report/powerbi/workspace";
import { useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { Page, PageStatus } from "src/components";
import { useApi } from "src/api";
import { merchantSettingsAtom, settingsAtom } from "src/components/FeatureFlagProvider/store/atoms";
import {
  mapMerchantSettingsToReportSettings,
  ReportSettingKey,
} from "src/components/FeatureFlagProvider/store/reportSettings";
import { useLoaders } from "src/components/InlineLoader";
import { useToasts } from "src/components/Toasts";
import { Button, Checkbox, Color, Flex, Select, Text } from "src/elements";
import { SelectOption } from "src/elements/Select/store/types";
import { LanguageSwitcher } from "src/i18n/components/LanguageSwitcher";
import { useTranslation } from "src/i18n";

type BooleanSettingKey =
  | "productRankingEnabled"
  | "maxUnitsEnabled"
  | "spaceMixEnabled"
  | "dosPercentage"
  | "jdaImportEnabled"
  | "shelfWeightEnabled";

type BooleanSettingLabel =
  | "productRanking"
  | "maxUnits"
  | "spaceMix"
  | "dosPercentage"
  | "jdaImport"
  | "shelfWeight";

type ReportSettingLabel = "basePog" | "storePog" | "singleStorePog" | "categoryRangeReview";

const BOOLEAN_SETTINGS: Array<{
  key: BooleanSettingKey;
  labelKey: BooleanSettingLabel;
}> = [
  { key: "productRankingEnabled", labelKey: "productRanking" },
  { key: "maxUnitsEnabled", labelKey: "maxUnits" },
  { key: "spaceMixEnabled", labelKey: "spaceMix" },
  { key: "dosPercentage", labelKey: "dosPercentage" },
  { key: "jdaImportEnabled", labelKey: "jdaImport" },
  { key: "shelfWeightEnabled", labelKey: "shelfWeight" },
];

const REPORT_SETTINGS: Array<{
  key: ReportSettingKey;
  labelKey: ReportSettingLabel;
}> = [
  { key: "basePogReportId", labelKey: "basePog" },
  { key: "storePogReportId", labelKey: "storePog" },
  { key: "singleStorePogReportId", labelKey: "singleStorePog" },
  { key: "categoryRangeReviewReportId", labelKey: "categoryRangeReview" },
];

const buildReportOptions = ({
  workspaceReports,
  currentValue,
  labels,
}: {
  workspaceReports: ResponseGetReportPowerBIWorkspaceReports | null | undefined;
  currentValue: string | null;
  labels: {
    notSet: string;
    createWorkspaceFirst: string;
    missingReport: (value: string) => string;
  };
}): SelectOption<string>[] => {
  const reportOptions =
    workspaceReports?.reports.map((report) => ({
      label: report.name,
      value: report.id,
    })) ?? [];

  const options: SelectOption<string>[] = [
    {
      label: workspaceReports ? labels.notSet : labels.createWorkspaceFirst,
      value: "",
    },
    ...reportOptions,
  ];

  if (currentValue && !reportOptions.some((option) => option.value === currentValue)) {
    options.push({
      label: labels.missingReport(currentValue),
      value: currentValue,
      isDisabled: true,
    });
  }

  return options;
};

export const PageSettings = () => {
  const {
    getMerchantSettingsApi,
    putMerchantSettingsApi,
    getPowerBIWorkspaceReportsApi,
    postPowerBIWorkspaceApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();
  const setPowerBISettingsAtom = useSetAtom(settingsAtom);
  const setMerchantSettingsAtom = useSetAtom(merchantSettingsAtom);
  const { t } = useTranslation("settings");

  const [merchantSettings, setMerchantSettings] = useState<
    ResponseGetMerchantSettings | null | undefined
  >(undefined);
  const [workspaceReports, setWorkspaceReports] = useState<
    ResponseGetReportPowerBIWorkspaceReports | null | undefined
  >(undefined);

  const isMerchantSettingsLoading = hasLoader(RouteGetMerchantSettings);
  const isMerchantSettingsSaving = hasLoader(RoutePutMerchantSettings);
  const isWorkspaceReportsLoading = hasLoader(RouteGetReportPowerBIWorkspaceReports);
  const isWorkspaceCreating = hasLoader(RoutePostReportPowerBIWorkspace);

  useEffect(() => {
    void loadSettingsPage();
  }, []);

  const loadSettingsPage = async () => {
    const [merchantSettingsResponse, workspaceReportsResponse] = await Promise.all([
      getMerchantSettingsApi(),
      getPowerBIWorkspaceReportsApi(),
    ]);

    setMerchantSettings(merchantSettingsResponse);
    setWorkspaceReports(workspaceReportsResponse);
    setMerchantSettingsAtom(merchantSettingsResponse);
    setPowerBISettingsAtom(mapMerchantSettingsToReportSettings(merchantSettingsResponse));
  };

  const updateBooleanSetting = async (key: BooleanSettingKey, value: boolean) => {
    if (!merchantSettings) {
      return;
    }

    const nextSettings: ResponseGetMerchantSettings = { ...merchantSettings, [key]: value };
    const payload: PayloadPutMerchantSettings = { [key]: value };

    setMerchantSettings(nextSettings);
    setMerchantSettingsAtom(nextSettings);
    const response = await putMerchantSettingsApi(payload);

    if (response) {
      toast({ type: "SUCCESS", title: t("toasts.saved") });
      return;
    }

    const freshSettings = await getMerchantSettingsApi();
    setMerchantSettings(freshSettings);
    setMerchantSettingsAtom(freshSettings);
  };

  const updateReportSetting = async (key: ReportSettingKey, value: string) => {
    if (!merchantSettings) {
      return;
    }

    const nextValue = value || null;
    const nextSettings: ResponseGetMerchantSettings = {
      ...merchantSettings,
      [key]: nextValue,
    };
    const payload: PayloadPutMerchantSettings = {
      [key]: nextValue,
    };

    setMerchantSettings(nextSettings);
    setMerchantSettingsAtom(nextSettings);
    setPowerBISettingsAtom(mapMerchantSettingsToReportSettings(nextSettings));
    const response = await putMerchantSettingsApi(payload);

    if (response) {
      toast({ type: "SUCCESS", title: t("toasts.saved") });
      return;
    }

    const freshSettings = await getMerchantSettingsApi();
    setMerchantSettings(freshSettings);
    setMerchantSettingsAtom(freshSettings);
    setPowerBISettingsAtom(mapMerchantSettingsToReportSettings(freshSettings));
  };

  const createWorkspace = async () => {
    const response = await postPowerBIWorkspaceApi();

    if (!response) {
      return;
    }

    toast({ type: "SUCCESS", title: t("toasts.workspaceCreated") });

    const refreshedWorkspaceReports = await getPowerBIWorkspaceReportsApi();
    setWorkspaceReports(
      refreshedWorkspaceReports ?? {
        workspaceId: response.workspaceId,
        workspaceName: response.workspaceName,
        reports: [],
      },
    );
  };

  const rows = useMemo(() => {
    if (!merchantSettings) {
      return [];
    }

    return [
      ...BOOLEAN_SETTINGS.map((setting) => ({
        id: setting.key,
        label: t(`booleanSettings.${setting.labelKey}.label` as const),
        description: t(`booleanSettings.${setting.labelKey}.description` as const),
        type: "boolean" as const,
        value: merchantSettings[setting.key],
      })),
      ...REPORT_SETTINGS.map((setting) => ({
        id: setting.key,
        label: t(`reportSettings.${setting.labelKey}.label` as const),
        description: t(`reportSettings.${setting.labelKey}.description` as const),
        type: "report" as const,
        value: merchantSettings[setting.key],
      })),
    ];
  }, [merchantSettings, t]);

  const isInitialLoading = merchantSettings === undefined || isMerchantSettingsLoading;

  if (isInitialLoading) {
    return (
      <Page>
        <PageStatus label={t("loading")} icon="spinner" />
      </Page>
    );
  }

  if (merchantSettings === null) {
    return (
      <Page>
        <PageStatus label={t("loadFailed")} icon="alert" color={Color.red} />
      </Page>
    );
  }

  return (
    <Page>
      <Flex column flexGrow gap="16px">
        <Flex padding="10px 0 0 0" justify="between" align="center" gap="12px" flexWrap="wrap">
          <Flex column gap="4px">
            <Text variant="h3">{t("pageTitle")}</Text>
            <Text variant="caption1" color={Color.textSecondary}>
              {t("pageSubtitle")}
            </Text>
          </Flex>

          <Flex gap="12px">
            {!workspaceReports && !isWorkspaceReportsLoading && (
              <Button color="greenSmoke" onClick={createWorkspace} isLoading={isWorkspaceCreating}>
                {t("createWorkspace")}
              </Button>
            )}
          </Flex>
        </Flex>

        <Flex
          background={Color.white}
          border={`1px solid ${Color.lightGrayHover}`}
          borderRadius="6px"
          padding="16px 20px"
          column
          gap="12px"
        >
          <Text variant="small1">{t("language")}</Text>
          <LanguageSwitcher />
        </Flex>

        <Flex column borderColor={Color.lightGrayHover} borderRadius="6px" overflow="hidden">
          {rows.map((row, index) => (
            <Flex
              key={row.id}
              justify="between"
              align="center"
              gap="20px"
              padding="16px 20px"
              flexWrap="wrap"
              borderBottom={
                index === rows.length - 1 ? undefined : `1px solid ${Color.lightGrayHover}`
              }
            >
              <Flex column gap="4px" flexGrow>
                <Text variant="small1">{row.label}</Text>
                <Text variant="caption1" color={Color.textSecondary}>
                  {row.description}
                </Text>
              </Flex>

              <Flex width="480px" maxWidth="100%" justify="right">
                {row.type === "boolean" ? (
                  <Checkbox
                    width="auto"
                    label={row.value ? t("enabled") : t("disabled")}
                    isChecked={row.value}
                    isDisabled={isMerchantSettingsSaving}
                    setIsChecked={(checked) => {
                      void updateBooleanSetting(row.id as BooleanSettingKey, checked);
                    }}
                  />
                ) : (
                  <Select
                    width="100%"
                    value={row.value ?? ""}
                    setValue={(value) => {
                      void updateReportSetting(row.id as ReportSettingKey, value);
                    }}
                    options={buildReportOptions({
                      workspaceReports,
                      currentValue: row.value,
                      labels: {
                        notSet: t("notSet"),
                        createWorkspaceFirst: t("createWorkspaceFirst"),
                        missingReport: (value) => t("missingReport", { value }),
                      },
                    })}
                    isLoading={isWorkspaceReportsLoading}
                    isDisabled={!workspaceReports || isMerchantSettingsSaving}
                  />
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Page>
  );
};
